import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success('Login berhasil');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark min-h-screen bg-background text-foreground grid grid-cols-1 lg:grid-cols-2">
      {/* Left panel - Brand */}
      <div className="hidden lg:flex relative flex-col justify-between p-10 border-r border-border overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: "linear-gradient(135deg, rgba(45,125,255,0.12) 0%, rgba(53,242,182,0.08) 100%), url('https://images.unsplash.com/photo-1635540636602-4b48b23a83d6?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-background/70" />
        <div className="relative">
          <div className="flex items-center">
            <img
              src="/assets/glowing-logo-horizontal.png"
              alt="Glowing Production"
              className="h-16 w-auto object-contain"
              data-testid="login-brand-logo-desktop"
            />
          </div>
          <div className="mt-2 text-xs text-muted-foreground tracking-wide">
            AR Tracker — Manajemen Penawaran & Piutang
          </div>
        </div>
        <div className="relative">
          <h1 className="font-heading text-3xl xl:text-4xl font-semibold tracking-tight leading-tight">
            Kelola penawaran, invoice, dan piutang event Anda
            <span className="text-primary"> dengan mudah.</span>
          </h1>
          <p className="text-muted-foreground mt-4 max-w-md">
            Pantau status pembayaran, aging piutang, dan follow-up client — semua dalam satu dashboard.
          </p>
        </div>
        <div className="relative text-xs text-muted-foreground">
          © {new Date().getFullYear()} Glowing Production
        </div>
      </div>

      {/* Right panel - Form */}
      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-6 flex items-center">
            <img
              src="/assets/glowing-logo-horizontal.png"
              alt="Glowing Production"
              className="h-10 w-auto object-contain"
              data-testid="login-brand-logo-mobile"
            />
          </div>

          <h2 className="font-heading text-2xl font-semibold">Masuk ke akun Anda</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">
            Gunakan email dan kata sandi untuk mengakses dashboard.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                placeholder="admin@glowing.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs">Kata Sandi</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPw ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                  aria-label={showPw ? 'Sembunyikan' : 'Tampilkan'}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              data-testid="login-submit-button"
              className="w-full"
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Masuk
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
